import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type SessionUser } from "../api/client";
import { CardTree } from "./CardTree";
import { CardEditor } from "./CardEditor";

export function ProjectView({ user }: { user: SessionUser | null }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = id!;
  const [project, setProject] = useState<any>(null);
  const [cards, setCards] = useState<any[]>([]);
  const [encounterSets, setEncounterSets] = useState<any[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [translations, setTranslations] = useState<any[]>([]);
  const translationCacheRef = React.useRef<Record<string, any>>({});
  const translationFetchesRef = React.useRef<Record<string, Promise<any>>>({});

  /** Récupère l'overlay complet d'une traduction, en le mettant en cache en
   * mémoire pour le reste de la session — un seul appel réseau par langue,
   * quel que soit le nombre de cartes consultées ensuite. Le parsing JSON a
   * lieu ici, côté navigateur (voir api.getTranslationRaw), jamais sur le
   * Worker. Passé tel quel à CardEditor. */
  async function getTranslationOverlay(lang: string): Promise<any> {
    if (translationCacheRef.current[lang]) return translationCacheRef.current[lang];
    if (!translationFetchesRef.current[lang]) {
      translationFetchesRef.current[lang] = api
        .getTranslationRaw(projectId, lang)
        .then((overlay) => {
          translationCacheRef.current[lang] = overlay;
          return overlay;
        })
        .finally(() => {
          delete translationFetchesRef.current[lang];
        });
    }
    return translationFetchesRef.current[lang];
  }

  /** Invalide le cache d'une langue (après une édition) pour que la
   * prochaine lecture reflète le changement — voir CardEditor. */
  function invalidateTranslationOverlay(lang: string) {
    delete translationCacheRef.current[lang];
  }
  const [reviewStatuses, setReviewStatuses] = useState<Record<string, string>>({});
  const [exportLang, setExportLang] = useState<string>("");
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [driveFolderInput, setDriveFolderInput] = useState("");
  const [savingDrive, setSavingDrive] = useState(false);
  const [refreshingDrive, setRefreshingDrive] = useState(false);
  const [driveMessage, setDriveMessage] = useState<string | null>(null);
  const [driveError, setDriveError] = useState<string | null>(null);

  const canWrite = !!user && (user.role === "admin" || (user.isGuildMember && user.hasRequiredRole));
  const isAdmin = user?.role === "admin";

  async function loadAll() {
    const [projectRes, cardsRes, translationsRes, statusesRes] = await Promise.all([
      api.getProject(projectId),
      api.listCards(projectId),
      api.listTranslations(projectId),
      api.listReviewStatuses(projectId), // langue originale par défaut (voir CardTree)
    ]);
    setProject(projectRes.project);
    setDriveFolderInput(projectRes.project.drive_folder_id ?? "");
    setCards(cardsRes.cards);
    setEncounterSets(cardsRes.encounterSets ?? []);
    setTranslations(translationsRes.translations);
    setReviewStatuses(statusesRes.statuses);
    if (!selectedCardId && cardsRes.cards.length > 0) setSelectedCardId(cardsRes.cards[0].id);
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function handleSync() {
    setSyncing(true);
    try {
      await api.syncProject(projectId);
      await loadAll();
    } finally {
      setSyncing(false);
    }
  }

  async function handleSaveDriveFolder(e: React.FormEvent) {
    e.preventDefault();
    setSavingDrive(true);
    setDriveMessage(null);
    setDriveError(null);
    try {
      const result = await api.setDriveFolder(projectId, driveFolderInput);
      setProject((p: any) => ({ ...p, drive_folder_id: result.folderId }));
      if (!result.folderId) {
        setDriveMessage("Dossier Drive retiré — les images retombent sur le repo GitHub seul.");
      } else if (result.warning) {
        setDriveError(`Dossier enregistré, mais l'indexation a échoué : ${result.warning}`);
      } else {
        setDriveMessage(`Dossier enregistré — ${result.filesIndexed ?? 0} fichier(s) indexé(s).`);
      }
    } catch (e: any) {
      setDriveError(e.message);
    } finally {
      setSavingDrive(false);
    }
  }

  async function handleRefreshDrive() {
    setRefreshingDrive(true);
    setDriveMessage(null);
    setDriveError(null);
    try {
      const result = await api.refreshDriveFolder(projectId);
      setDriveMessage(`Index Drive à jour — ${result.filesIndexed} fichier(s).`);
    } catch (e: any) {
      setDriveError(e.message);
    } finally {
      setRefreshingDrive(false);
    }
  }

  async function handleDelete() {
    const confirmed = window.confirm(
      `Supprimer « ${project.name} » du registre ? Toutes les traductions, notes et rendus en cache pour ce projet seront perdus. Le repo GitHub d'origine n'est pas affecté. Cette action est irréversible.`
    );
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await api.admin.deleteProject(projectId);
      navigate("/");
    } catch (e: any) {
      setDeleteError(e.message);
      setDeleting(false);
    }
  }

  if (!project) return <p>Chargement du projet…</p>;

  return (
    <div className="project-view">
      <header className="project-view__header">
        <div>
          <h1>{project.name}</h1>
          <p className="project-view__repo">
            {project.repo_owner}/{project.repo_name} — {cards.length} carte(s)
          </p>
        </div>
        <div className="project-view__actions">
          <button className="btn btn--ghost" onClick={handleSync} disabled={syncing}>
            {syncing ? "Resynchronisation…" : "Resynchroniser depuis GitHub"}
          </button>
          {canWrite && (
            <div className="export-control">
              <select value={exportLang} onChange={(e) => setExportLang(e.target.value)}>
                <option value="">Exporter : original</option>
                {translations.map((t) => (
                  <option key={t.id} value={t.lang_code}>
                    Exporter : {t.lang_code}
                  </option>
                ))}
              </select>
              <a
                className="btn btn--primary"
                href={api.exportProjectUrl(projectId, exportLang || undefined)}
                download
              >
                Exporter le JSON
              </a>
            </div>
          )}
          {isAdmin && (
            <button className="btn btn--danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Suppression…" : "Supprimer le projet"}
            </button>
          )}
        </div>
      </header>

      {deleteError && <p className="error">{deleteError}</p>}

      {canWrite && (
        <details className="drive-settings">
          <summary>Images — dépôt GitHub{project.drive_folder_id ? " + Google Drive" : ""}</summary>
          <form className="drive-settings__form" onSubmit={handleSaveDriveFolder}>
            <p className="drive-settings__hint">
              Si tes images ne sont pas commitées dans le repo GitHub (art trop volumineux), colle ici l'URL ou
              l'ID d'un dossier Google Drive <strong>partagé publiquement</strong> ("Accès général → Tous les
              utilisateurs disposant du lien"). Les images introuvables sur GitHub seront alors recherchées par
              nom de fichier dans ce dossier.
            </p>
            <div className="drive-settings__row">
              <input
                className="input"
                placeholder="https://drive.google.com/drive/folders/... ou l'ID seul"
                value={driveFolderInput}
                onChange={(e) => setDriveFolderInput(e.target.value)}
              />
              <button className="btn btn--small" type="submit" disabled={savingDrive}>
                {savingDrive ? "Enregistrement…" : "Enregistrer"}
              </button>
              {project.drive_folder_id && (
                <button
                  type="button"
                  className="btn btn--small btn--ghost"
                  onClick={handleRefreshDrive}
                  disabled={refreshingDrive}
                >
                  {refreshingDrive ? "Actualisation…" : "Rafraîchir les images Drive"}
                </button>
              )}
            </div>
            {driveMessage && <p className="drive-settings__message">{driveMessage}</p>}
            {driveError && <p className="error">{driveError}</p>}
          </form>
        </details>
      )}

      <div className="project-view__body">
        <CardTree
          cards={cards}
          encounterSets={encounterSets}
          selectedCardId={selectedCardId}
          onSelect={setSelectedCardId}
          statusByCardId={reviewStatuses as any}
        />

        <main className="project-view__main">
          {selectedCardId ? (
            <CardEditor
              projectId={projectId}
              cardId={selectedCardId}
              cards={cards}
              getTranslationOverlay={getTranslationOverlay}
              invalidateTranslationOverlay={invalidateTranslationOverlay}
              user={user}
              canWrite={canWrite}
              onOriginalStatusChange={(status) =>
                setReviewStatuses((prev) => ({ ...prev, [selectedCardId]: status }))
              }
            />
          ) : (
            <p>Sélectionne une carte dans la liste.</p>
          )}
        </main>
      </div>
    </div>
  );
}
